import type { AxiosError} from "axios";
import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { env } from "../env";
import { useAuthStore } from "@/features/auth/stores/authStore";
import { usePlanGateStore } from "@/features/subscription/stores/planGateStore";

export const apiClient: AxiosInstance = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 30_000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // required for HttpOnly refresh-token cookie
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  const lang = document.documentElement.lang || "pt-BR";
  config.headers.set("Accept-Language", lang);
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const status = error.response?.status;

    if (status === 401 && original && !original._retry) {
      original._retry = true;
      // Renovação single-flight compartilhada — vive no authStore (o SSE usa a mesma).
      const newToken = await useAuthStore.getState().refresh();
      if (newToken) {
        original.headers?.set("Authorization", `Bearer ${newToken}`);
        return apiClient.request(original);
      }
      // Refresh failed — clear local session immediately (state) and fire-and-forget server revocation
      const store = useAuthStore.getState();
      void store.logout();
    }

    // Gate de assinatura: 402 subscription_required → ABRE o modal de ativação de plano
    // (hub#489) em vez de redirecionar — isso matava o loop /dashboard↔/onboarding/plan
    // (hub#389). O modal busca o motivo (org_seat_revoked/never) e deixa o corretor decidir.
    // Fora do cadastro/onboarding (incl. /register), onde a conta está sendo criada e gatear
    // quebraria o fluxo.
    if (status === 402) {
      const code = (error.response?.data as { code?: string } | undefined)?.code;
      const path = window.location.pathname;
      const inSignupFlow = path.startsWith("/onboarding") || path.startsWith("/register");
      if (code === "subscription_required" && !inSignupFlow) {
        usePlanGateStore.getState().openGate();
      }
    }

    return Promise.reject(error);
  },
);
